import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormDialog } from '@/components/shared/form-dialog';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FundSelect } from '@/components/shared/fund-select';
import {
  isOvernightProgramTimeRange,
  isProgramTimeRangeInvalid,
} from '@/components/programs/program-time';
import { Tip } from '@/components/shared/tip';
import { VenueSelect } from '@/components/shared/venue-select';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { FormFieldError } from '@/components/ui/form-field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { MultiDatePicker } from '@/components/ui/multi-date-picker';
import { SwitchField } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { TimePicker } from '@/components/ui/time-picker';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { resolveDefaultFund } from '@/lib/giving-default-funds';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { churchToday } from '@/utils/format';
import { useGivingSettings } from '@/hooks/use-giving-settings';

import { ProgramBannerField } from './program-image-fields';
import {
  type Gathering,
  type GatheringKind,
  type ServicePattern,
  SERVICE_PATTERNS,
  PROGRAM_PATTERNS,
  WEEKDAYS,
  parseServicePattern,
  patternToNth,
  nextWeekdayOnOrAfter,
} from './gathering-schedule';

export interface GatheringFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: GatheringKind;
  /** When set, the dialog edits this gathering instead of creating one. */
  gathering?: Gathering | null;
  /** Prefills create dates (program start/end, or a special service date). */
  defaultDate?: string | null;
  onSaved?: (gathering: Gathering) => void;
}

export const GatheringFormDialog = ({
  open,
  onOpenChange,
  kind,
  gathering = null,
  defaultDate = null,
  onSaved,
}: GatheringFormDialogProps) => {
  const { t } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const church = useChurchStore((state) => state.church);
  const baseCurrency = church?.baseCurrency ?? 'GHS';
  const canEdit = hasPermission(membership, PERMISSIONS.PROGRAMS_EDIT);
  const ns = kind === 'service' ? 'services' : 'programs';
  const editing = gathering ?? null;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pattern, setPattern] = useState<ServicePattern>(kind === 'program' ? 'special' : 'weekly');
  const [weekday, setWeekday] = useState('7');
  const [specificDates, setSpecificDates] = useState<string[]>([]);
  const [startsOn, setStartsOn] = useState<string | null>(churchToday());
  const [endsOn, setEndsOn] = useState<string | null>(churchToday());
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('12:00');
  const [venueId, setVenueId] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState('');
  const [joinRequiresCode, setJoinRequiresCode] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [coverImageKey, setCoverImageKey] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const initialCoverImageKeyRef = useRef<string | null>(null);
  const [isPublished, setIsPublished] = useState(true);
  const [showOnApp, setShowOnApp] = useState(true);
  const [appVisibility, setAppVisibility] = useState<'public' | 'members_only'>('members_only');
  const [gpsAutoCheckInEnabled, setGpsAutoCheckInEnabled] = useState(true);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [inviteOnly, setInviteOnly] = useState(false);
  const [capacity, setCapacity] = useState('');
  const [feeType, setFeeType] = useState<'free' | 'paid'>('free');
  const [feeAmountMinor, setFeeAmountMinor] = useState<number | null>(null);
  const [feeCurrency, setFeeCurrency] = useState<string | null>(null);
  const [registrationFundId, setRegistrationFundId] = useState<string | null>(null);
  const [allowMobileMoney, setAllowMobileMoney] = useState(true);
  const [allowCard, setAllowCard] = useState(true);
  const [allowCash, setAllowCash] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);

  const weekdayOptions = useMemo(
    () => WEEKDAYS.map((value) => ({ value, label: t(`weekdays.${value}`) })),
    [t],
  );

  const patternOptions = useMemo(
    () =>
      (kind === 'program' ? PROGRAM_PATTERNS : SERVICE_PATTERNS).map((value) => ({
        value,
        label: t(`${ns}.patterns.${value}`),
      })),
    [kind, ns, t],
  );

  const givingSettings = useGivingSettings();
  const registrationFunds = useQuery({
    queryKey: ['programs', 'registration-funds'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; defaultCurrency: string }> }>(
        '/programs/registration-funds',
      ),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? '');
      const parsed = parseServicePattern(editing);
      setPattern(parsed.pattern);
      setWeekday(parsed.weekday);
      setSpecificDates(parsed.specificDates);
      setStartsOn(editing.startsOn ?? churchToday());
      setEndsOn(editing.endsOn ?? editing.startsOn ?? churchToday());
      setStartTime(editing.startTime ?? '10:00');
      setEndTime(editing.endTime ?? '12:00');
      setVenueId(editing.venueId);
      setJoinUrl(editing.joinUrl ?? '');
      setJoinRequiresCode(editing.joinRequiresCode === true);
      setJoinCode(editing.joinCode ?? '');
      initialCoverImageKeyRef.current = editing.coverImageKey;
      setCoverImageKey(editing.coverImageKey);
      setCoverImagePreview(editing.coverImageUrl);
      setIsPublished(editing.isActive);
      const visibleInApp = editing.visibility !== 'internal';
      setShowOnApp(visibleInApp);
      setAppVisibility(editing.visibility === 'public' ? 'public' : 'members_only');
      setGpsAutoCheckInEnabled(editing.gpsAutoCheckInEnabled === true);
      setRegistrationEnabled(editing.registrationMode !== 'none' && editing.registrationMode !== undefined);
      setInviteOnly(editing.registrationAccess === 'invite_only');
      setCapacity(editing.capacity ? String(editing.capacity) : '');
      setFeeType(editing.registrationFeeType ?? 'free');
      setFeeAmountMinor(editing.registrationFeeAmountMinor ?? null);
      setFeeCurrency(editing.registrationFeeCurrency ?? baseCurrency);
      setRegistrationFundId(editing.registrationFundId ?? null);
      setAllowMobileMoney(
        editing.registrationAllowedPaymentMethods?.includes('mobile_money') ?? true,
      );
      setAllowCard(editing.registrationAllowedPaymentMethods?.includes('card') ?? true);
      setAllowCash(editing.registrationAllowedPaymentMethods?.includes('cash') ?? false);
      return;
    }
    setName('');
    setDescription('');
    setPattern(kind === 'program' ? 'special' : 'weekly');
    setWeekday('7');
    setSpecificDates([]);
    setShowOnApp(true);
    setAppVisibility('members_only');
    setGpsAutoCheckInEnabled(true);
    setRegistrationEnabled(false);
    setInviteOnly(false);
    setCapacity('');
    setFeeType('free');
    setFeeAmountMinor(null);
    setFeeCurrency(baseCurrency);
    setRegistrationFundId(null);
    setAllowMobileMoney(true);
    setAllowCard(true);
    setAllowCash(false);
    const initialDate = defaultDate ?? churchToday();
    setStartsOn(initialDate);
    setEndsOn(initialDate);
    setStartTime('10:00');
    setEndTime('12:00');
    setVenueId(null);
    setJoinUrl('');
    setJoinRequiresCode(false);
    setJoinCode('');
    initialCoverImageKeyRef.current = null;
    setCoverImageKey(null);
    setCoverImagePreview(null);
    setIsPublished(true);
    // Reset when the dialog opens or the edited gathering changes — not on every
    // parent re-render of the same gathering object.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed by id / open
  }, [
    open,
    editing?.id,
    editing?.updatedAt,
    editing?.coverImageKey,
    editing?.coverImageUrl,
    defaultDate,
    kind,
  ]);

  useEffect(() => {
    if (!open || editing) return;
    const defaults = resolveDefaultFund(
      givingSettings.data,
      'eventRegistration',
      registrationFunds.data?.data?.map((fund) => ({ ...fund, isActive: true })),
    );
    if (defaults.fundId) {
      setRegistrationFundId(defaults.fundId);
    }
  }, [open, editing, givingSettings.data, registrationFunds.data?.data]);

  const schedulePayload = () => {
    if (pattern === 'dates') {
      const dates = [...specificDates].sort();
      return {
        scheduleType: 'custom' as const,
        scheduleRule: dates.join(','),
        startsOn: dates[0] ?? null,
        endsOn: dates[dates.length - 1] ?? null,
      };
    }
    if (pattern === 'special') {
      return {
        scheduleType: 'one_off' as const,
        scheduleRule: startsOn,
        startsOn,
        endsOn: kind === 'program' ? (endsOn ?? startsOn) : startsOn,
      };
    }
    if (pattern === 'weekly') {
      return {
        scheduleType: 'weekly' as const,
        scheduleRule: weekday,
        startsOn: null,
        endsOn: null,
      };
    }
    if (pattern === 'fortnight') {
      return {
        scheduleType: 'weekly' as const,
        scheduleRule: `fortnight:${weekday}`,
        startsOn: nextWeekdayOnOrAfter(startsOn ?? churchToday(), weekday),
        endsOn: null,
      };
    }
    return {
      scheduleType: 'monthly' as const,
      scheduleRule: `nth:${patternToNth[pattern]}:${weekday}`,
      startsOn: null,
      endsOn: null,
    };
  };

  const registrationPayload = () => {
    if (kind !== 'program' || !registrationEnabled) {
      return {
        registrationMode: 'none' as const,
        registrationAccess: 'open' as const,
        registrationFeeType: 'free' as const,
        registrationFeeAmountMinor: null,
        registrationFeeCurrency: null,
        registrationFundId: null,
        registrationAllowedPaymentMethods: ['mobile_money', 'card'] as Array<'mobile_money' | 'card'>,
        capacity: null,
      };
    }

    const methods: Array<'mobile_money' | 'card' | 'cash'> = [];
    if (allowMobileMoney) methods.push('mobile_money');
    if (allowCard) methods.push('card');
    if (allowCash) methods.push('cash');

    return {
      registrationMode: 'registration' as const,
      registrationAccess: inviteOnly ? ('invite_only' as const) : ('open' as const),
      registrationFeeType: feeType,
      registrationFeeAmountMinor: feeType === 'paid' ? feeAmountMinor : null,
      registrationFeeCurrency: feeType === 'paid' ? feeCurrency ?? baseCurrency : null,
      registrationFundId: feeType === 'paid' ? registrationFundId : null,
      registrationAllowedPaymentMethods: methods,
      capacity: capacity.trim() ? Number(capacity) : null,
    };
  };

  const save = useMutation({
    mutationFn: async () => {
      const trimmedDescription = description.trim();
      const descriptionValue = trimmedDescription.length > 0 ? trimmedDescription : null;
      const trimmedJoinUrl = joinUrl.trim();
      const joinUrlValue = trimmedJoinUrl.length > 0 ? trimmedJoinUrl : null;
      const trimmedJoinCode = joinCode.trim();
      const joinCodeValue = joinRequiresCode && trimmedJoinCode.length > 0 ? trimmedJoinCode : null;

      if (editing) {
        const coverImagePatch =
          coverImageKey !== initialCoverImageKeyRef.current ? { coverImageKey } : {};
        return api.patch<Gathering>(`/programs/${editing.id}`, {
          name,
          description: descriptionValue,
          startTime,
          endTime,
          venueId,
          joinUrl: joinUrlValue,
          joinRequiresCode,
          joinCode: joinCodeValue,
          ...coverImagePatch,
          gpsAutoCheckInEnabled,
          visibility: showOnApp ? appVisibility : 'internal',
          ...schedulePayload(),
          ...registrationPayload(),
        });
      }

      return api.post<Gathering>('/programs', {
        name,
        description: descriptionValue,
        kind,
        startTime,
        endTime,
        venueId,
        joinUrl: joinUrlValue,
        joinRequiresCode,
        joinCode: joinCodeValue,
        coverImageKey,
        gpsAutoCheckInEnabled,
        visibility: showOnApp ? 'members_only' : 'internal',
        ...schedulePayload(),
        ...(kind === 'program' ? registrationPayload() : {}),
      });
    },
    onSuccess: (row) => {
      toast.success(t(`${ns}.saved`));
      queryClient.setQueryData(['programs', row.id], row);
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
      onOpenChange(false);
      onSaved?.(row);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: () => api.post<Gathering>(`/programs/${editing!.id}/publish`),
    onSuccess: (row) => {
      toast.success(t(`${ns}.published`));
      setIsPublished(true);
      setPublishOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
      void queryClient.invalidateQueries({ queryKey: ['programs', editing!.id] });
      onSaved?.(row);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: () => api.post<Gathering>(`/programs/${editing!.id}/unpublish`),
    onSuccess: (row) => {
      toast.success(t(`${ns}.unpublished`));
      setIsPublished(false);
      setUnpublishOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
      void queryClient.invalidateQueries({ queryKey: ['programs', editing!.id] });
      onSaved?.(row);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const needsDate = pattern === 'special';
  const needsSpecificDates = pattern === 'dates';
  const timeRangeInvalid = isProgramTimeRangeInvalid(startTime, endTime);
  const timeRangeOvernight = isOvernightProgramTimeRange(startTime, endTime);
  const paidRegistrationInvalid =
    registrationEnabled &&
    feeType === 'paid' &&
    (!feeAmountMinor ||
      feeAmountMinor <= 0 ||
      !registrationFundId ||
      (!allowMobileMoney && !allowCard && !allowCash));

  const canSubmit =
    name.trim().length > 0 &&
    !coverUploading &&
    !timeRangeInvalid &&
    !paidRegistrationInvalid &&
    (!joinRequiresCode || joinCode.trim().length > 0) &&
    (!needsDate || Boolean(startsOn)) &&
    (!needsSpecificDates || specificDates.length >= 2);

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title={editing ? t(`${ns}.edit`) : t(`${ns}.create`)}
        size="lg"
        closeLabel={tCommon('action.close')}
        cancelLabel={tCommon('action.cancel')}
        submitLabel={t(`${ns}.save`)}
        submitting={save.isPending}
        submitDisabled={!canSubmit}
        footerLeading={
          editing && canEdit ? (
            isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={save.isPending || publish.isPending || unpublish.isPending}
                onClick={() => setUnpublishOpen(true)}
              >
                {t('unpublish')}
              </Button>
            ) : (
              <Button
                type="button"
                disabled={save.isPending || publish.isPending || unpublish.isPending}
                onClick={() => setPublishOpen(true)}
              >
                {t('publish')}
              </Button>
            )
          ) : undefined
        }
        onSubmit={() => {
          if (timeRangeInvalid) return;
          save.mutate();
        }}
      >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>{t(`${ns}.name`)}</Label>
          <Input value={name} onChange={(event) => setName(event.target.value)} required />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t(`${ns}.descriptionField`)}</Label>
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            maxLength={4000}
            showCount
          />
          <Tip tipId={`${ns}.descriptionField`} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t(`${ns}.pattern`)}</Label>
          <Listbox
            value={pattern}
            onChange={(value) => setPattern((value as ServicePattern) ?? 'weekly')}
            options={patternOptions}
            popoverPortal="dialog"
          />
        </div>
        {pattern === 'dates' ? (
          <div className="flex flex-col gap-1.5">
            <Label>{t('programs.specificDays')}</Label>
            <MultiDatePicker
              value={specificDates}
              onChange={setSpecificDates}
              popoverPortal="dialog"
              minCount={2}
            />
            <Tip tipId="programs.specificDays" />
          </div>
        ) : pattern === 'special' ? (
          kind === 'program' ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>{t('programs.startsOn')}</Label>
                <DatePicker
                  value={startsOn}
                  onChange={(value) => {
                    setStartsOn(value);
                    if (value && endsOn && endsOn < value) setEndsOn(value);
                  }}
                  popoverPortal="dialog"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('programs.endsOn')}</Label>
                <DatePicker value={endsOn} onChange={setEndsOn} popoverPortal="dialog" />
                <Tip tipId="programs.endsOn" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label>{t('services.specialDate')}</Label>
              <DatePicker value={startsOn} onChange={setStartsOn} popoverPortal="dialog" />
            </div>
          )
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label>{t('services.weekday')}</Label>
            <Listbox
              value={weekday}
              onChange={(value) => setWeekday(value ?? '7')}
              options={weekdayOptions}
              popoverPortal="dialog"
            />
          </div>
        )}
        <div className="space-y-1.5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label>{t('startTime')}</Label>
              <TimePicker
                value={startTime}
                onChange={(value) => setStartTime(value ?? '10:00')}
                popoverPortal="dialog"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label>{t('endTime')}</Label>
              <TimePicker
                value={endTime}
                onChange={(value) => setEndTime(value ?? '12:00')}
                popoverPortal="dialog"
              />
            </div>
          </div>
          <Tip tipId="programs.churchTimezone" />
          {timeRangeOvernight ? (
            <p className="text-caption text-fg-secondary">{t('timeRangeOvernightHint')}</p>
          ) : null}
          {timeRangeInvalid ? <FormFieldError message={t('timeRangeInvalid')} /> : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('venue')}</Label>
          <VenueSelect value={venueId} onChange={setVenueId} popoverPortal="dialog" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('joinOnline')}</Label>
          <Input
            inputMode="url"
            autoComplete="url"
            value={joinUrl}
            onChange={(event) => setJoinUrl(event.target.value)}
          />
          <Tip tipId="programs.joinOnline" />
        </div>
        <SwitchField
          id="join-requires-code"
          label={t('joinRequiresCode')}
          description={t('joinRequiresCodeHint')}
          checked={joinRequiresCode}
          onCheckedChange={setJoinRequiresCode}
        />
        {joinRequiresCode ? (
          <div className="flex flex-col gap-1.5">
            <Label>{t('joinCode')}</Label>
            <Input
              value={joinCode}
              onChange={(event) => setJoinCode(event.target.value)}
              autoComplete="off"
              maxLength={80}
            />
            <Tip tipId="programs.joinCode" />
          </div>
        ) : null}
        <ProgramBannerField
          idPrefix={kind === 'service' ? 'service' : 'program'}
          variant={kind === 'service' ? 'service' : 'program'}
          imageKey={coverImageKey}
          previewUrl={coverImagePreview}
          onUploadingChange={setCoverUploading}
          onChange={({ imageKey, previewUrl }) => {
            setCoverImageKey(imageKey);
            setCoverImagePreview(previewUrl);
          }}
        />
        <SwitchField
          id="show-on-app"
          label={t('showOnApp')}
          description={t('showOnAppHint')}
          checked={showOnApp}
          onCheckedChange={setShowOnApp}
        />
        <SwitchField
          id="gps-auto-check-in"
          label={t(`${ns}.gpsAutoCheckIn`)}
          description={t(`${ns}.gpsAutoCheckInHint`)}
          checked={gpsAutoCheckInEnabled}
          onCheckedChange={setGpsAutoCheckInEnabled}
        />
        {kind === 'program' ? (
          <div className="space-y-4 border-t border-border pt-6">
            <SwitchField
              id="registration-enabled"
              label={t('registration.enable')}
              description={t('registration.enableHint')}
              checked={registrationEnabled}
              onCheckedChange={setRegistrationEnabled}
            />
            {registrationEnabled ? (
              <>
                <SwitchField
                  id="registration-invite-only"
                  label={t('registration.inviteOnly')}
                  description={t('registration.inviteOnlyHint')}
                  checked={inviteOnly}
                  onCheckedChange={setInviteOnly}
                />
                <div className="flex flex-col gap-1.5">
                  <Label>{t('registration.capacity')}</Label>
                  <Input
                    inputMode="numeric"
                    value={capacity}
                    onChange={(event) => setCapacity(event.target.value.replace(/\D/g, ''))}
                    placeholder={t('registration.capacityPlaceholder')}
                  />
                  <p className="text-caption text-fg-secondary">{t('registration.capacityPlaceholder')}</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>{t('registration.feeType')}</Label>
                  <Listbox
                    value={feeType}
                    onChange={(value) => setFeeType((value as 'free' | 'paid') ?? 'free')}
                    options={[
                      { value: 'free', label: t('registration.feeFree') },
                      { value: 'paid', label: t('registration.feePaid') },
                    ]}
                    popoverPortal="dialog"
                  />
                </div>
                {feeType === 'paid' ? (
                  <div className="space-y-4">
                    <div className="flex flex-col gap-1.5">
                      <Label>{t('registration.feeAmount')}</Label>
                      <MoneyInput
                        value={feeAmountMinor}
                        onChange={setFeeAmountMinor}
                        currency={feeCurrency ?? baseCurrency}
                        onCurrencyChange={setFeeCurrency}
                        popoverPortal="dialog"
                      />
                      <p className="text-caption text-fg-secondary">
                        {t('registration.paidOnlineHint')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>{t('registration.fund')}</Label>
                      <FundSelect
                        value={registrationFundId}
                        onChange={(fundId) => setRegistrationFundId(fundId)}
                        popoverPortal="dialog"
                        dataSource="registration"
                        emptyMessage={t('registration.fundEmpty')}
                      />
                      <p className="text-caption text-fg-secondary">
                        {t('registration.fundHint')}
                      </p>
                    </div>
                    <div className="space-y-3">
                      <Label>{t('registration.paymentMethods')}</Label>
                      <SwitchField
                        id="registration-mobile-money"
                        label={t('registration.mobileMoney')}
                        checked={allowMobileMoney}
                        onCheckedChange={setAllowMobileMoney}
                      />
                      <SwitchField
                        id="registration-card"
                        label={t('registration.card')}
                        checked={allowCard}
                        onCheckedChange={setAllowCard}
                      />
                      <SwitchField
                        id="registration-cash"
                        label={t('registration.cash')}
                        description={t('registration.cashHint')}
                        checked={allowCash}
                        onCheckedChange={setAllowCash}
                      />
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
        </div>
      </FormDialog>

      {editing ? (
        <>
          <ConfirmDialog
            open={publishOpen}
            onOpenChange={setPublishOpen}
            title={t(`confirm.publish.title.${kind}`, { name: editing.name })}
            description={t(`confirm.publish.description.${kind}`)}
            confirmLabel={t(`confirm.publish.confirm.${kind}`)}
            cancelLabel={tCommon('action.cancel')}
            loading={publish.isPending}
            onConfirm={() => publish.mutate()}
          />
          <ConfirmDialog
            open={unpublishOpen}
            onOpenChange={setUnpublishOpen}
            title={t(`confirm.unpublish.title.${kind}`, { name: editing.name })}
            description={t(`confirm.unpublish.description.${kind}`)}
            confirmLabel={t(`confirm.unpublish.confirm.${kind}`)}
            cancelLabel={tCommon('action.cancel')}
            loading={unpublish.isPending}
            onConfirm={() => unpublish.mutate()}
          />
        </>
      ) : null}
    </>
  );
};
